import React, {useState} from "react";
import axios from "axios";
import {useNavigate} from "react-router-dom";
import {Eye, EyeOff, KeyRound, LogOut, ShieldCheck} from "lucide-react";
import full_icon from "../assets/yekkada-logo-new.png";
import * as Constants from "../utils/Constants";
import {getAuthToken, getClientIdOfUser, getLoggedInUser, getLoggedInUserId, showToast} from "../utils/utility";
import {getRole, landingPathForRole} from "../utils/rolePermissions";
import {logoutFun} from "./HomeSideNavBar";
import Button from "../components/ui/Button";
import TextField from "../components/ui/TextField";
import {cn} from "../utils/cn";

const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@#$])[A-Za-z\d@#$]{8,}$/;

/**
 * Shown exactly once, right after a Track Point (MOBI_TRACK) driver's first WhatsApp-OTP
 * login — their account has no real password yet (LoginResponse/User.passwordSet === false; the
 * backend stores an unguessable random placeholder instead of one that could be shared/leaked).
 * Mirrors the Android app's SetInitialPasswordActivity, which shipped first — this closes the
 * matching gap on the web client, whose Change Password (ChangePassword.js) requires an old
 * password the driver has no way to know, and whose login routing (LandingPage) had no
 * awareness of passwordSet at all, silently sending a driver straight to Live Tracking with no
 * way to ever set a working password.
 *
 * Reached from two places, matching the Android app's two entry points: LandingPage's
 * post-login redirect (a fresh WhatsApp-OTP login response with passwordSet: false), and
 * App.js's PrivateRoute (redirects here on every protected route while the persisted session
 * still has passwordSet: false — covers a driver who navigated away or reloaded mid-setup).
 */
const SetInitialPassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  /** Marks the session as password-set locally so PrivateRoute stops redirecting here, then
   *  sends the driver on to their normal landing page — mirrors Android's
   *  SessionManager.savePasswordSet(true) + PostLoginSetup.navigateByRole. */
  const proceedToLanding = () => {
    const stored = getLoggedInUser();
    if (stored) {
      stored.passwordSet = true;
      localStorage.setItem(Constants.LOGGED_IN_USER, JSON.stringify(stored));
    }
    navigate(landingPathForRole(getRole()), {replace: true});
  };

  const submit = async () => {
    setErrorMsg("");
    if (!newPassword || !confirmNewPassword) {
      setErrorMsg("Enter and confirm your new password.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMsg("Passwords do not match.");
      return;
    }
    if (!passwordRegex.test(newPassword)) {
      setErrorMsg(
        "Password must be 8+ chars, include letters, numbers and one of @ # $. Only these symbols allowed."
      );
      return;
    }

    try {
      setLoading(true);
      const endpoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/users/setInitialPassword/${getLoggedInUserId()}`;
      await axios.put(
        endpoint,
        {newPassword, confirmNewPassword},
        {headers: {Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json"}}
      );
      showToast("Password set", {type: "success"});
      proceedToLanding();
    } catch (e) {
      // 409 = the backend already considers this account's password set (e.g. set from another
      // tab/device, or a retried request that actually landed the first time). Either way
      // there's nothing left to do here but move on — stranding the driver on this screen
      // forever would be worse than a silent no-op (mirrors the Android screen's same rule).
      if (e?.response?.status === 409) {
        proceedToLanding();
        return;
      }
      setErrorMsg(e?.response?.data?.responseMessage || "Could not set password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter") submit();
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-surface-muted">
      {/* BRAND PANEL */}
      <aside className="lg:flex-1 lg:max-w-[640px] bg-gradient-to-br from-brand-700 to-brand-900 text-white px-6 sm:px-10 py-8 lg:py-12 flex flex-col">
        <div className="w-fit bg-white rounded-lg px-3 py-1.5 shadow-sm">
          <img src={full_icon} alt="Yekkada" className="h-8 w-auto" />
        </div>
        <div className="flex-1 flex items-center">
          <div className="max-w-lg">
            <p className="text-xs uppercase tracking-wider font-semibold text-accent-300">One-time setup</p>
            <h1 className="text-3xl lg:text-4xl font-bold leading-tight mt-3">
              You're signed in.<br />
              <span className="text-accent-400">Set your password.</span>
            </h1>
            <p className="text-base text-white/80 mt-4 leading-relaxed">
              For quicker sign-ins next time, set a password now — you can still use WhatsApp OTP
              to log in later too.
            </p>
          </div>
        </div>
        <p className="text-xs text-white/60 mt-6">© 2026 Yekkada · Satyology Solutions Pvt Ltd</p>
      </aside>

      {/* FORM PANEL */}
      <section className="flex-1 px-6 sm:px-10 py-10 lg:py-12 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-brand-700" />
            <h2 className="text-2xl font-bold text-ink-900">Set your password</h2>
          </div>

          {errorMsg && (
            <div className="mt-3 rounded-lg bg-danger-50 border border-danger-100 px-4 py-3 text-sm text-danger-700">
              {errorMsg}
            </div>
          )}

          <div className="mt-6 space-y-4">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-ink-700">
                New password <span className="text-danger-600">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={onEnter}
                  placeholder="Pick a strong password"
                  autoComplete="new-password"
                  autoFocus
                  className={cn(
                    "block w-full h-9 pl-3 pr-10 text-base rounded-md",
                    "bg-white border border-ink-200 shadow-inset",
                    "text-ink-900 placeholder:text-ink-400",
                    "hover:border-ink-300",
                    "focus:border-brand-500 focus:outline-none focus:ring"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-ink-400 hover:text-ink-600 hover:bg-ink-100"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <TextField
              label="Confirm new password"
              required
              type={showPassword ? "text" : "password"}
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              onKeyDown={onEnter}
              placeholder="Re-enter the password"
              autoComplete="new-password"
            />

            <div className="rounded-lg bg-info-50 border border-info-100 px-4 py-3 text-sm text-info-700 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                Minimum 8 characters. Must contain letters, numbers, and at least one symbol from
                <span className="font-mono mx-1 bg-white/60 px-1 rounded">@ # $</span>.
                Other special characters are not allowed.
              </div>
            </div>

            <Button variant="primary" size="lg" className="w-full" onClick={submit} loading={loading}>
              Set password
            </Button>
            <Button
              variant="link"
              className="w-full inline-flex items-center justify-center gap-1.5"
              onClick={logoutFun}
            >
              <LogOut className="h-3.5 w-3.5" /> Log out instead
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SetInitialPassword;
